/**
 * JIT audio S3 paths (lexicon + cache) for the Ubercourse runtime.
 * Must stay aligned with mod-content `esm/audioJit/constants.js`.
 *
 * Prefer `VITE_AUDIO_JIT_ENV` (folder name). Alternatively `VITE_ENV_TYPE` +
 * `VITE_ENV_NAME` when type is `local`, matching mod-content ENV_TYPE/ENV_NAME.
 */

const DEFAULT_AUDIO_JIT_BUCKET = 'vinciworks-courses-audio';
const DEFAULT_AUDIO_JIT_ENV = 'dev';
const AUDIO_JIT_S3_REGION = 'eu-west-2';

const trim = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const viteEnv = import.meta.env;

export function resolveAudioJitBucket(): string {
  return trim(viteEnv.VITE_AUDIO_JIT_BUCKET_NAME) || DEFAULT_AUDIO_JIT_BUCKET;
}

export function resolveAudioJitEnvFolder(): string {
  const explicit = trim(viteEnv.VITE_AUDIO_JIT_ENV);
  if (explicit) return explicit;

  const envType = trim(viteEnv.VITE_ENV_TYPE);
  if (envType === 'local') {
    const envName = trim(viteEnv.VITE_ENV_NAME);
    if (!envName) {
      console.warn(
        '[audioJit] VITE_ENV_TYPE=local but VITE_ENV_NAME is unset. ' +
          'Set VITE_AUDIO_JIT_ENV (or VITE_ENV_NAME) to match mod-content, e.g. nathan-local-cp. ' +
          `Using "${DEFAULT_AUDIO_JIT_ENV}" for S3 paths.`
      );
      return DEFAULT_AUDIO_JIT_ENV;
    }
    return envName;
  }

  if (envType) return envType;

  return DEFAULT_AUDIO_JIT_ENV;
}

export function buildAudioJitLexiconBaseUrl(
  bucket: string,
  envFolder: string
): string {
  return `https://${bucket}.s3.dualstack.${AUDIO_JIT_S3_REGION}.amazonaws.com/audio-ai-jit-lexicons/${envFolder}`;
}

export function buildAudioJitCacheBaseUrl(
  bucket: string,
  envFolder: string
): string {
  return `https://${bucket}.s3.amazonaws.com/audio-ai-jit-cache/${envFolder}`;
}

export const AUDIO_JIT_BUCKET = resolveAudioJitBucket();
export const AUDIO_JIT_ENV = resolveAudioJitEnvFolder();

/** Base URL for JIT lexicon files (not legacy `audio-ai/voice-settings`). */
export const AUDIO_JIT_LEXICON_BASE_URL =
  trim(viteEnv.VITE_AUDIO_JIT_LEXICON_BASE_URL) ||
  buildAudioJitLexiconBaseUrl(AUDIO_JIT_BUCKET, AUDIO_JIT_ENV);

/** Base URL for reading cached JIT MP3 objects from S3. */
export const AUDIO_JIT_CACHE_BASE_URL =
  trim(viteEnv.VITE_AUDIO_JIT_CACHE_BASE_URL) ||
  buildAudioJitCacheBaseUrl(AUDIO_JIT_BUCKET, AUDIO_JIT_ENV);

/** S3 folder path under the bucket (includes env subfolder). */
export const AUDIO_JIT_CACHE_FOLDER = `audio-ai-jit-cache/${AUDIO_JIT_ENV}`;
